import { memo, useCallback, useRef, useState } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ApiService } from 'api';
import { StyledTableCell, StyledTableRow } from 'dashboard409a/components/ValuationsTable/common';
import InlineNoteEditor from 'dashboard409a/components/ValuationsTable/common/InlineNoteEditor';
import { useCellEditEnd } from 'dashboard409a/hooks/useCellEditEnd';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { MODELS_COLUMN_IDS } from 'dashboard409a/pages/Workboard/common/constants';
import { getFullName, renderStatusCell, renderValuationLeadCell } from './helpers';
import { getModelStatusString } from '../../../services/models.types';
import { ModelsStatusDropdown } from '../ModelsStatusDropdown/ModelsStatusDropdown';
import { ModelsTableRowProps } from '../ModelsTable.types';

const ModelsTableRow = memo(({
  modelRow,
  onRowClick,
  onNoteSave,
  onStatusUpdate,
  isNoteLoading = false,
  visibleColumnIds,
}: ModelsTableRowProps) => {
  const isVisible = (id: string) => !visibleColumnIds || visibleColumnIds.has(id);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isNoteCellActive, setIsNoteCellActive] = useState(false);
  const statusCellRef = useRef<HTMLTableCellElement>(null);

  const isCompleteStatus = modelRow.model_status === modelStatusMap.COMPLETE;
  
  const handleStatusChange = useCallback(async (newStatus: string) => {
    try {
      setIsUpdatingStatus(true);

      const apiStatusValue = modelStatusMap[newStatus as keyof typeof modelStatusMap];
      if (apiStatusValue === undefined) {
        enqueueSnackbar('Invalid status selected', { variant: 'error' });
        return;
      }

      const updatedModelRow = { model_status: apiStatusValue, company_measurement_date: modelRow.company_measurement_date };
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
        modelRow?.company_id.toString(), 
        modelRow?.attio_valuation_id, 
        updatedModelRow
      );

      if (onStatusUpdate && modelRow.id) {
        onStatusUpdate(modelRow.id, apiStatusValue);
      }

      setIsEditingStatus(false);
    } catch {
      enqueueSnackbar('Failed to update model status', { variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [modelRow, onStatusUpdate]);

  const handleNoteSave = useCallback(async (newNote: string): Promise<void> => {
    if (onNoteSave && modelRow.id) {
      const currentNoteContent = modelRow.note?.content || modelRow.notes || '';
      await onNoteSave(modelRow.id, newNote, currentNoteContent);
    }
  }, [onNoteSave, modelRow]);

  const handleStatusEditEnd = useCellEditEnd(statusCellRef, () => setIsEditingStatus(false));

  const handleStatusClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUpdatingStatus && !isCompleteStatus) {
      setIsEditingStatus(true);
    }
  }, [isUpdatingStatus, isCompleteStatus]);

  return (
    <StyledTableRow
      hover
      tabIndex={-1}
      key={modelRow.id}
      onClick={event => onRowClick(modelRow, event)}>
      {/* company_name is always visible */}
      <StyledTableCell align="left">
        <span>{modelRow.company_name}</span> &nbsp;
        {modelRow.is_loading_valuation_data ? (
          <Skeleton component="span" variant="text" width={80} sx={{ display: 'inline-block', ml: 0.5, mr: 1, verticalAlign: 'middle' }} />
        ) : (
          modelRow.engagement_type || ''
        )}
      </StyledTableCell>
      {isVisible(MODELS_COLUMN_IDS.VALUATION_STATUS) && (
        <StyledTableCell align="left">
          <Typography variant="body2">
            {modelRow.is_loading_valuation_data ? (
              <Skeleton variant="text" width="80%" height={20} />
            ) : (
              modelRow.valuation_status || '-'
            )}
          </Typography>
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.MODEL_STATUS) && (
        <StyledTableCell ref={statusCellRef} align="left" onClick={handleStatusClick}>
          <ModelsStatusDropdown
            currentStatus={getModelStatusString(modelRow.model_status || 0)}
            onStatusChange={handleStatusChange}
            isEditing={isEditingStatus}
            onEditEnd={handleStatusEditEnd}
          />

          {renderStatusCell({
            modelRow,
            isEditingStatus,
            isUpdatingStatus,
            isCompleteStatus,
            onStatusClick: handleStatusClick,
            onEditEnd: handleStatusEditEnd,
          })}
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.SUPPORTING_DUE_DATE) && (
        <StyledTableCell
          align="left"
          onClick={e => e.stopPropagation()}
          sx={{
            p: 0,
            overflow: 'hidden',
            maxWidth: '220px',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}>
          <Typography variant="body2">
            {modelRow.supporting_due_date || '-'}
          </Typography>
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.NOTES) && (
        <StyledTableCell
          align="left"
          onClick={e => e.stopPropagation()}
          sx={{
            p: 0,
            overflow: 'hidden',
            maxWidth: '220px',
            boxShadow: isNoteCellActive ? 'inset 0 0 0 1px #1976d2' : 'none',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}>
          <InlineNoteEditor
            value={modelRow.note?.content || modelRow.notes}
            onSave={handleNoteSave}
            isLoading={isNoteLoading}
            onActiveStateChange={setIsNoteCellActive}
          />
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.MODEL_REVIEWER) && (
        <StyledTableCell align="left">
          <Typography variant="body2">
            {modelRow.reviewers?.[0] ? getFullName(modelRow.reviewers?.[0]) : '-'}
            {modelRow.reviewers && modelRow.reviewers?.length > 1 && `, ${modelRow.reviewers?.length - 1} more`}
          </Typography>
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.MODEL_PREPARER) && (
        <StyledTableCell align="left">
          <Typography variant="body2">
            {modelRow.preparers?.[0] ? getFullName(modelRow.preparers?.[0]) : '-'}
            {modelRow.preparers && modelRow.preparers?.length > 1 && `, ${modelRow.preparers?.length - 1} more`}
          </Typography>
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.VALUATION_LEAD) && (
        <StyledTableCell align="left">
          {renderValuationLeadCell(modelRow)}
        </StyledTableCell>
      )}
      {isVisible(MODELS_COLUMN_IDS.VALUATION_DATE) && (
        <StyledTableCell align="left">
          <Typography variant="body2">
            {modelRow.is_loading_valuation_data ? (
              <Skeleton variant="text" width="80%" height={20} />
            ) : (
              modelRow.valuation_date || '-'
            )}
          </Typography>
        </StyledTableCell>
      )}
    </StyledTableRow>
  );
});

export default ModelsTableRow;
